import { useEffect, useLayoutEffect, useRef } from 'react';
import { num, str } from './blockRender.js';
import { sliceLines, textLeaves } from '../../sections/_shared/revealChoreo.js';
import { paintClippedGradient } from '../../sections/_shared/clippedTextGradient.js';
const SPLIT_UNITS = ['words', 'chars', 'lines'];
const SPLIT_FROMS = ['bottom', 'top', 'left', 'right'];
const asUnit = (v) => SPLIT_UNITS.includes(v) ? v : 'words';
const asFrom = (v) => SPLIT_FROMS.includes(v) ? v : 'bottom';
/**
 * The Shrine animation inspector fields (shared so heading + text expose the identical controls).
 *
 * A SECTION can carry them too, for the words it paints itself — the caption over an image or a video
 * (21/09/2026: "le texte dans l'image n'est pas animé du tout"). `group` files them under that text's
 * own heading, and `when` gates every one of them on that text existing (an animation for a sentence
 * nobody wrote is noise).
 */
export function animationFields(opts = {}) {
    const g = opts.group ?? 'Animations';
    const anim = 'textAnimation';
    const gate = (f) => {
        if (!opts.when)
            return f;
        const own = f.visibleWhen === undefined ? [] : [].concat(f.visibleWhen);
        return { ...f, visibleWhen: [...own, opts.when] };
    };
    return animationFieldList(g, anim).map(gate);
}
function animationFieldList(g, anim) {
    return [
        {
            id: anim,
            type: 'select',
            label: 'Animation type',
            group: g,
            info: 'A reveal effect that plays when the block scrolls into view ("Shiny" and "Blink" play continuously).',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'split', label: 'Split' },
                { value: 'shiny', label: 'Shiny' },
                { value: 'blink', label: 'Blink' },
                { value: 'blur', label: 'Blur' },
                { value: 'typing', label: 'Typing' },
            ],
        },
        {
            id: 'shinySpeed',
            type: 'range',
            label: 'Shine speed',
            group: g,
            min: 1,
            max: 20,
            step: 1,
            default: 5,
            unit: 's',
            visibleWhen: { field: anim, equals: 'shiny' },
        },
        {
            // Same id, range and unit as the urgency block's own `blinkSpeed`, so the two read as one control.
            id: 'blinkSpeed',
            type: 'range',
            label: 'Blink speed',
            group: g,
            info: 'Duration of one on-off cycle.',
            min: 300,
            max: 3000,
            step: 100,
            default: 1000,
            unit: 'ms',
            visibleWhen: { field: anim, equals: 'blink' },
        },
        {
            id: 'splitType',
            type: 'select',
            label: 'Split by',
            group: g,
            info: 'Lines are the ones the browser actually shows: a heading written one word per line reveals one word after another.',
            default: 'words',
            options: [
                { value: 'words', label: 'Words' },
                { value: 'chars', label: 'Characters' },
                { value: 'lines', label: 'Lines' },
            ],
            visibleWhen: { field: anim, equals: 'split' },
        },
        {
            id: 'splitFrom',
            type: 'select',
            label: 'Direction',
            group: g,
            default: 'bottom',
            options: [
                { value: 'bottom', label: 'From below' },
                { value: 'top', label: 'From above' },
                { value: 'left', label: 'From the left' },
                { value: 'right', label: 'From the right' },
            ],
            visibleWhen: { field: anim, equals: 'split' },
        },
        {
            id: 'animStagger',
            type: 'range',
            label: 'Stagger',
            group: g,
            info: 'Delay between two words, characters or lines. Two lines never follow each other closer than 90 ms.',
            min: 10,
            max: 200,
            step: 10,
            default: 40,
            unit: 'ms',
            visibleWhen: { field: anim, equals: 'split' },
        },
        {
            id: 'animYOffset',
            type: 'range',
            label: 'Distance',
            group: g,
            min: 0,
            max: 80,
            step: 4,
            default: 24,
            unit: 'px',
            // A line travels its own height from behind its mask; a pixel distance means nothing to it.
            visibleWhen: [
                { field: anim, equals: 'split' },
                { field: 'splitType', notEquals: 'lines' },
            ],
        },
        {
            id: 'animDuration',
            type: 'range',
            label: 'Reveal duration',
            group: g,
            min: 100,
            max: 2000,
            step: 50,
            default: 600,
            unit: 'ms',
            visibleWhen: { field: anim, equals: ['split', 'blur'] },
        },
        {
            id: 'typingSpeed',
            type: 'range',
            label: 'Typing speed',
            group: g,
            min: 10,
            max: 200,
            step: 5,
            default: 45,
            unit: 'ms',
            visibleWhen: { field: anim, equals: 'typing' },
        },
    ];
}
/** Read the animation settings into a resolved config. */
export function readTextAnimation(bs) {
    return {
        type: str(bs.textAnimation, 'none'),
        splitUnit: asUnit(str(bs.splitType, 'words')),
        from: asFrom(str(bs.splitFrom, 'bottom')),
        stagger: num(bs.animStagger, 40),
        duration: num(bs.animDuration, 600),
        yOffset: num(bs.animYOffset, 24),
        typingSpeed: num(bs.typingSpeed, 45),
    };
}
/**
 * "Shiny" — a continuous CSS shimmer sweeping a light band across the glyphs (gradient clipped to the
 * text, keyframed background-position). Pure CSS, no observer. `color` is the text's own colour, so the
 * band rides over whatever the seller chose; reduced motion pins it.
 */
export function shinyTextCss(selector, keyframe, color, speedS) {
    return (`@keyframes ${keyframe}{to{background-position:-200% 0}}` +
        `${selector}{` +
        `background:linear-gradient(110deg,${color} 0%,${color} 40%,rgba(255,255,255,0.85) 50%,${color} 60%,${color} 100%);` +
        `background-size:200% 100%;-webkit-background-clip:text;background-clip:text;` +
        `-webkit-text-fill-color:transparent;color:transparent;` +
        `animation:${keyframe} ${speedS}s linear infinite}` +
        `@media(prefers-reduced-motion:reduce){${selector}{animation:none}}`);
}
/** The blink's floor and ceiling, whatever a stored document carries (a 0 ms cycle is a strobe). */
const BLINK_MIN_MS = 300;
const BLINK_MAX_MS = 3000;
/** The seller's blink cycle, clamped: the range field's own bounds, re-applied to what the document says. */
export function readBlinkSpeed(bs) {
    const v = num(bs.blinkSpeed, 1000);
    return Math.min(BLINK_MAX_MS, Math.max(BLINK_MIN_MS, v));
}
/**
 * "Blink" — the text fades to almost nothing and back, forever. Pure CSS (keyframed opacity on the
 * element itself), no observer, SSR-safe: the storefront hydrates no page block, so like Shiny this must
 * live in the block's own stylesheet or it does not exist on a live shop. Opacity only: the glyphs keep
 * their colour, their gradient and their highlights — a blinking gradient heading stays a gradient
 * heading. Reduced motion pins it at full opacity. The urgency block's own switch paints the same curve.
 */
export function blinkTextCss(selector, keyframe, speedMs) {
    return (`@keyframes ${keyframe}{0%,100%{opacity:1}50%{opacity:.12}}` +
        `${selector}{animation:${keyframe} ${speedMs}ms ease-in-out infinite}` +
        `@media(prefers-reduced-motion:reduce){${selector}{animation:none}}`);
}
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
/** The attribute that names the effect on the SSR'd element — the storefront engine's only entry point. */
export const TEXTANIM_ATTR = 'data-aqx-textanim';
/** Set by whichever runtime got there first, so the hook and the vanilla engine can never both apply. */
export const TEXTANIM_BOUND_ATTR = 'data-aqx-ta-bound';
/**
 * The SSR attributes that let a NON-React host drive this animation.
 *
 * WHY THIS EXISTS (founder, 2026-09-18: "l'animation split sur le texte dans image+texte ne fonctionne
 * pas"). The effect used to live ONLY in {@link useTextAnimation}, a React hook. The builder canvas is
 * live React, so it played there — and the STOREFRONT never hydrates a page block at all
 * (`StorefrontPage.astro` carries no `client:` directive), so on every real shop Split, Blur and Typing
 * did precisely nothing, on every block, in every section. Only Shiny worked, because Shiny is pure CSS.
 *
 * Returns `{}` for `none`/`shiny`/`blink` so a block that asked for no engine work stays byte-identical
 * (the two continuous effects are CSS the block itself emits).
 */
export function textAnimationAttrs(cfg) {
    if (cfg.type === 'none' || cfg.type === 'shiny' || cfg.type === 'blink')
        return {};
    const attrs = { [TEXTANIM_ATTR]: cfg.type };
    if (cfg.type === 'split') {
        attrs['data-aqx-ta-unit'] = cfg.splitUnit;
        attrs['data-aqx-ta-from'] = cfg.from;
        attrs['data-aqx-ta-y'] = String(cfg.yOffset);
    }
    if (cfg.type === 'split' || cfg.type === 'blur') {
        attrs['data-aqx-ta-stagger'] = String(cfg.stagger);
        attrs['data-aqx-ta-duration'] = String(cfg.duration);
    }
    if (cfg.type === 'typing')
        attrs['data-aqx-ta-speed'] = String(cfg.typingSpeed);
    return attrs;
}
/** Read {@link textAnimationAttrs} back off a server-rendered element. Every value is guarded: the HTML
 *  comes from a stored document, so it can hold anything a past or future version wrote. */
export function readTextAnimationAttrs(el) {
    const type = el.getAttribute(TEXTANIM_ATTR);
    if (type !== 'split' && type !== 'blur' && type !== 'typing')
        return null;
    const n = (name, fallback) => {
        // The raw string is checked for emptiness FIRST: `Number('')` is 0, which is finite and non-negative,
        // so an attribute present but empty used to read as a deliberate "0 ms" — a whole cascade collapsing
        // into one flash. A missing value is not a value. (Adversarial test: a stagger of "".)
        const raw = el.getAttribute(name);
        if (raw === null || raw.trim() === '')
            return fallback;
        const v = Number(raw);
        return Number.isFinite(v) && v >= 0 ? v : fallback;
    };
    return {
        type,
        splitUnit: asUnit(el.getAttribute('data-aqx-ta-unit')),
        from: asFrom(el.getAttribute('data-aqx-ta-from')),
        stagger: n('data-aqx-ta-stagger', 40),
        duration: n('data-aqx-ta-duration', 600),
        yOffset: n('data-aqx-ta-y', 24),
        typingSpeed: n('data-aqx-ta-speed', 45),
    };
}
/**
 * The caps that keep "Split" from turning into a missing paragraph. See the note at the call site.
 * 240 units at the squeezed step is still a visible cascade; beyond it nobody reads a per-letter reveal.
 */
const MAX_SPLIT_UNITS = 240;
/** The whole cascade is over within this, however many units — the last word must not arrive late. */
const MAX_SPLIT_SPAN_MS = 1200;
/** A typewriter is done inside this however long the text: 900 characters at 45 ms would be 40 s. */
const MAX_TYPING_SPAN_MS = 6000;
/** Below this a line cascade reads as one flash; the seller's 40 ms word step is a floor, not a value. */
const LINE_MIN_STEP_MS = 90;
/** More lines than this and the block fades as one piece — a 30-line reveal is a wait, not an effect. */
const MAX_LINES = 24;
/** How far a line travels from behind its mask, as a share of its own height. */
const LINE_TRAVEL = '115%';
/** The out-expo curve the unveil uses: fast off the mark, long soft landing. */
const LINE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
/** The wrapper (mask) and inner (mover) of a line the BLOCK engine cut — distinct from the unveil's. */
export const TA_LINE_ATTR = 'data-aqx-ta-line';
export const TA_LINE_UNIT_ATTR = 'data-aqx-ta-lineunit';
/** How many spans {@link wrapItems} WOULD create — counted without touching the DOM. */
function countUnits(el, unit) {
    const text = el.textContent ?? '';
    if (unit === 'chars')
        return Array.from(text).filter((c) => !/\s/.test(c)).length;
    return text.split(/\s+/).filter((w) => w !== '').length;
}
/**
 * Wrap each word/char of every text node under `el` in an inline-block span (preserves <strong>/<em>).
 * In character mode the characters of one word sit inside a `white-space: nowrap` span: adjacent
 * inline-blocks are break opportunities, so without it a long word could wrap in its middle.
 */
function wrapItems(el, unit) {
    const spans = [];
    const doc = el.ownerDocument;
    const item = (text) => {
        const span = doc.createElement('span');
        span.textContent = text;
        span.style.display = 'inline-block';
        span.style.willChange = 'transform, opacity';
        spans.push(span);
        return span;
    };
    const walk = (node) => {
        const kids = Array.from(node.childNodes);
        for (const child of kids) {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent ?? '';
                if (!text.trim())
                    continue;
                const frag = doc.createDocumentFragment();
                for (const part of text.split(/(\s+)/)) {
                    if (part === '')
                        continue;
                    if (/^\s+$/.test(part)) {
                        frag.appendChild(doc.createTextNode(part));
                        continue;
                    }
                    if (unit === 'words') {
                        frag.appendChild(item(part));
                        continue;
                    }
                    const word = doc.createElement('span');
                    word.style.whiteSpace = 'nowrap';
                    for (const ch of Array.from(part))
                        word.appendChild(item(ch));
                    frag.appendChild(word);
                }
                child.replaceWith(frag);
            }
            else if (child.nodeType === Node.ELEMENT_NODE) {
                walk(child);
            }
        }
    };
    walk(el);
    return spans;
}
/**
 * Wrap every character (whitespace included, so a space "types" too) in a plain inline span, hidden.
 * Plain inline — not inline-block — so the wrapping of the text is exactly what it was, and every
 * `<br>`, `<p>`, `<strong>` stays where the author put it. The typewriter then shows them one by one.
 */
function wrapTyping(el) {
    const spans = [];
    const doc = el.ownerDocument;
    const walk = (node) => {
        for (const child of Array.from(node.childNodes)) {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent ?? '';
                if (text === '')
                    continue;
                const frag = doc.createDocumentFragment();
                for (const ch of Array.from(text)) {
                    const span = doc.createElement('span');
                    span.textContent = ch;
                    span.style.visibility = 'hidden';
                    spans.push(span);
                    frag.appendChild(span);
                }
                child.replaceWith(frag);
            }
            else if (child.nodeType === Node.ELEMENT_NODE) {
                walk(child);
            }
        }
    };
    walk(el);
    return spans;
}
/** The resting-start transform of a word/char coming from `from`, `d` pixels away. */
function travel(from, d) {
    switch (from) {
        case 'top':
            return `translate3d(0, ${-d}px, 0)`;
        case 'left':
            return `translate3d(${-d}px, 0, 0)`;
        case 'right':
            return `translate3d(${d}px, 0, 0)`;
        default:
            return `translate3d(0, ${d}px, 0)`;
    }
}
/**
 * Apply one reveal effect to one element and return the cleanup. THE single implementation — the React
 * hook and the storefront's vanilla init both come through here, so the builder canvas and the live shop
 * can never drift into two different animations.
 *
 * `shiny`/`none` never reach it (they are CSS, or nothing).
 */
export function applyTextAnimation(el, cfg) {
    const noop = () => { };
    // THE FAILSAFE, AND IT COMES FIRST. theme.css hides `[data-aqx-textanim]` before first paint (under the
    // same armed flag the section reveals use) so the reader never sees the text land and then jump. Every
    // path that decides NOT to animate therefore has to put it back, or the block is simply invisible —
    // which is a far worse bug than a missing animation.
    const show = () => {
        el.style.opacity = '1';
    };
    // The two continuous effects are the block's own CSS: nothing to observe, nothing to take apart.
    if (cfg.type === 'none' || cfg.type === 'shiny' || cfg.type === 'blink') {
        show();
        return noop;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
        show();
        return noop;
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
        show();
        return noop;
    }
    const originalHtml = el.innerHTML;
    const win = el.ownerDocument.defaultView ?? window;
    let cancelled = false;
    let timer = 0;
    let spans = [];
    let restoreLines = null;
    // Whatever this engine put inside the element, remembered so the restores below only ever undo OUR
    // work. In the builder, React rewrites the innerHTML the moment the seller edits the text: putting the
    // mount-time HTML back over her new sentence would show her the OLD one until her next keystroke.
    let ours = [];
    const claim = () => {
        ours = Array.from(el.childNodes);
    };
    const intact = () => ours.length === el.childNodes.length && ours.every((n, i) => el.childNodes[i] === n);
    const putBack = () => {
        if (intact())
            el.innerHTML = originalHtml;
    };
    const fadeWhole = () => {
        el.style.opacity = '0';
        el.style.transition = `opacity ${cfg.duration}ms ease`;
    };
    // ── Prepare the hidden initial state (before paint, so there's no flash of final content). ──
    if (cfg.type === 'blur') {
        el.style.opacity = '0';
        el.style.filter = 'blur(12px)';
        el.style.transition = `opacity ${cfg.duration}ms ease, filter ${cfg.duration}ms ease`;
    }
    else if (cfg.type === 'split' && cfg.splitUnit === 'lines') {
        // Lines are measured where the browser put them, and the browser puts them when the element is in
        // view at its final width — so the slicing waits for `play()`. Until then the block stays hidden.
        el.style.opacity = '0';
    }
    else if (cfg.type === 'split') {
        // THE UNIT IS CHOSEN BEFORE WRAPPING, NOT AFTER. "Characters" on a paragraph is a setting a seller
        // can pick in two clicks, and taken literally it is two bugs at once: 900 spans each carrying
        // `will-change` (900 compositor layers on a phone), and — at the default 40ms step — a last letter
        // that arrives after THIRTY-SIX SECONDS. Text that is invisible for half a minute is not an
        // animation, it is a missing paragraph. So a run too long for per-character falls back to words, and
        // one too long even for words fades in as one piece.
        const wanted = cfg.splitUnit === 'chars' ? 'chars' : 'words';
        const unit = countUnits(el, wanted) > MAX_SPLIT_UNITS ? 'words' : wanted;
        if (countUnits(el, unit) <= MAX_SPLIT_UNITS)
            spans = wrapItems(el, unit);
        if (spans.length === 0) {
            // Nothing to stagger (empty block, or a run too long even by words) → the whole element fades,
            // which is the honest degradation: it still reveals on scroll, just in one piece.
            fadeWhole();
            spans = [];
        }
        // The step is squeezed so the WHOLE reveal is over inside MAX_SPLIT_SPAN_MS however many units there
        // are: the seller sets the feel, the cap stops the feel from becoming a wait.
        const step = spans.length > 1 ? Math.min(cfg.stagger, MAX_SPLIT_SPAN_MS / (spans.length - 1)) : 0;
        spans.forEach((s, i) => {
            s.style.opacity = '0';
            s.style.transform = travel(cfg.from, cfg.yOffset);
            s.style.transition = `opacity ${cfg.duration}ms ease ${i * step}ms, transform ${cfg.duration}ms ease ${i * step}ms`;
        });
        // A gradient heading paints its ink from the element; a moving span is a layer of its own that the
        // parent's ink cannot reach (nothing painted at all, in Chromium). Each unit takes its slice here —
        // see clippedTextGradient.ts. AFTER the start state above, never before: this measure is the first
        // style these spans ever get, and a first style starts no transition. Read before the start state
        // is written and the start state itself becomes a transition (from "none"), which the end state
        // then cancels — the words simply appear. The measure undoes the start translation itself.
        paintClippedGradient(spans);
        // The spans carry the hiding, so the CONTAINER has to be visible or nothing will ever show — but
        // ONLY when there are spans. With none, the element itself is the thing fading in (set just above),
        // and showing it here would cancel that fade and make the whole reveal a no-op. Caught by the
        // adversarial test "a run too long even by words fades as one piece rather than staying hidden".
        if (spans.length > 0)
            show();
        claim();
    }
    else {
        // Typing: the text is all there, laid out, and every character is invisible. Nothing moves when it
        // types — the seven-line heading keeps its seven lines from the first frame.
        spans = wrapTyping(el);
        show();
        claim();
    }
    /** "Split by → Lines": cut the element into its rendered lines and slide each one in, in turn. */
    const playLines = () => {
        const slices = textLeaves(el)
            .map((leaf) => sliceLines(leaf, {
            lineAttr: TA_LINE_ATTR,
            unitAttr: TA_LINE_UNIT_ATTR,
            maxLines: MAX_LINES,
        }))
            .filter((s) => s !== null);
        const inners = slices.flatMap((s) => s.inners);
        if (inners.length === 0) {
            // Not a box we can measure, or nothing in it: the block fades in as one piece rather than staying
            // hidden — the same honest degradation as an over-long word run.
            slices.forEach((s) => s.restore());
            fadeWhole();
            void el.offsetWidth;
            show();
            return;
        }
        restoreLines = () => slices.forEach((s) => s.restore());
        claim();
        const D = cfg.duration;
        const step = inners.length > 1
            ? Math.min(Math.max(cfg.stagger, LINE_MIN_STEP_MS), MAX_SPLIT_SPAN_MS / (inners.length - 1))
            : 0;
        const vertical = cfg.from === 'top' || cfg.from === 'bottom';
        for (const w of slices.flatMap((s) => s.wrappers)) {
            w.style.display = 'block';
            // The mask is on the STATIC wrapper, never on the mover: a clip on the thing that moves moves with
            // it. -0.2em top/bottom keeps ascenders and descenders that overhang the line box.
            if (vertical)
                w.style.clipPath = 'inset(-0.2em 0px)';
        }
        inners.forEach((inner, i) => {
            const delay = Math.round(i * step);
            inner.style.display = 'block';
            if (vertical) {
                inner.style.willChange = 'transform';
                inner.style.transform = `translate3d(0, ${cfg.from === 'top' ? '-' : ''}${LINE_TRAVEL}, 0)`;
                inner.style.transition = `transform ${D}ms ${LINE_EASE} ${delay}ms`;
            }
            else {
                // Sideways, a line is WIPED open from its edge (and nudged a little), whatever its alignment:
                // sliding a short centred line the full width of its box would only show in its last frames.
                inner.style.willChange = 'clip-path, transform';
                inner.style.clipPath =
                    cfg.from === 'left' ? 'inset(-0.2em 100% -0.2em 0px)' : 'inset(-0.2em 0px -0.2em 100%)';
                inner.style.transform = `translate3d(${cfg.from === 'left' ? '-' : ''}0.4em, 0, 0)`;
                inner.style.transition = `clip-path ${D}ms ${LINE_EASE} ${delay}ms, transform ${D}ms ${LINE_EASE} ${delay}ms`;
            }
        });
        // A gradient heading's ink goes onto each line here, so the line still shows its slice of the
        // gradient while it slides (see clippedTextGradient.ts). After the start state, not before: the
        // measure is these lines' first style, and a first style starts no transition; a style read before
        // the start state would make the start state a transition of its own (see the words above).
        paintClippedGradient(inners);
        // Commit the start state, then the end state — that is what makes a transition run.
        void el.offsetWidth;
        show();
        inners.forEach((inner) => {
            inner.style.transform = 'translate3d(0, 0, 0)';
            if (!vertical)
                inner.style.clipPath = 'inset(-0.2em 0px)';
        });
        // Once every line has landed, the element gets its own nodes back — so a later resize, a hover
        // colour or the seller's next edit meet the markup React wrote, not our slices.
        const total = Math.round((inners.length - 1) * step) + D + 100;
        timer = win.setTimeout(() => {
            if (cancelled)
                return;
            restoreLines?.();
            restoreLines = null;
        }, total);
    };
    const play = () => {
        if (cancelled)
            return;
        if (cfg.type === 'blur') {
            el.style.opacity = '1';
            el.style.filter = 'blur(0)';
        }
        else if (cfg.type === 'split' && cfg.splitUnit === 'lines') {
            playLines();
        }
        else if (cfg.type === 'split') {
            // `spans` is empty when the run was too long to take apart — the element itself carries the fade.
            el.style.opacity = '1';
            spans.forEach((s) => {
                s.style.opacity = '1';
                s.style.transform = 'translate3d(0, 0, 0)';
            });
            if (spans.length > 0) {
                // Once the last unit has landed, the element gets its own markup back — as lines and typing
                // already do. No span keeps a compositor layer alive, and a gradient heading is painted by ONE
                // element again: its per-word copies would go stale at the first reflow.
                const step = spans.length > 1 ? Math.min(cfg.stagger, MAX_SPLIT_SPAN_MS / (spans.length - 1)) : 0;
                const total = Math.round((spans.length - 1) * step) + cfg.duration + 100;
                timer = win.setTimeout(() => {
                    if (!cancelled)
                        putBack();
                }, total);
            }
        }
        else {
            // The step is squeezed so the whole text is typed inside MAX_TYPING_SPAN_MS: a paragraph at
            // 45 ms/char would otherwise take longer than anyone stays on the page.
            const n = spans.length;
            const step = n > 0 ? Math.min(cfg.typingSpeed, MAX_TYPING_SPAN_MS / n) : cfg.typingSpeed;
            let i = 0;
            const tick = () => {
                if (cancelled)
                    return;
                if (i < n) {
                    spans[i].style.visibility = '';
                    i += 1;
                    timer = win.setTimeout(tick, step);
                }
                else {
                    putBack(); // the pristine markup once typed out, formatting and all
                }
            };
            tick();
        }
    };
    const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
            if (e.isIntersecting) {
                play();
                io.disconnect();
                break;
            }
        }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => {
        cancelled = true;
        io.disconnect();
        if (timer)
            win.clearTimeout(timer);
        // Restore the pristine markup + clear inline animation styles (builder live-edit safety). Only OUR
        // markup is undone: if the seller's edit already replaced it, hers stays.
        if (restoreLines) {
            restoreLines();
            restoreLines = null;
        }
        else {
            putBack();
        }
        el.style.opacity = '';
        el.style.filter = '';
        el.style.transition = '';
    };
}
/**
 * Wire every server-rendered `[data-aqx-textanim]` under `root`. This is what the STOREFRONT runs — it
 * ships no React for page blocks, so without this call the effect does not exist there at all.
 *
 * Returns a cleanup so a live host can re-bind without leaking observers; the storefront's static global
 * init ignores it. Idempotent: an element already driven by the React hook (or by a previous call) carries
 * `data-aqx-ta-bound` and is stepped over, so the two runtimes can never both wrap the same words.
 */
export function initTextAnimations(root) {
    const els = Array.from(root.querySelectorAll(`[${TEXTANIM_ATTR}]`)).filter((el) => !el.hasAttribute(TEXTANIM_BOUND_ATTR));
    if (!els.length)
        return () => { };
    // Tell the <head> snippet's dead-man's timer the animation half of the page is alive. `initReveals`
    // sets the same flag, but it returns EARLY when the page holds no animated section — and a page can
    // hold a split heading and not one animated band, which would un-arm the flag under our own feet.
    window['__aqxRevealReady'] = true;
    const cleanups = els.map((el) => {
        const cfg = readTextAnimationAttrs(el);
        if (!cfg) {
            el.style.opacity = '1';
            return () => { };
        }
        el.setAttribute(TEXTANIM_BOUND_ATTR, '');
        const off = applyTextAnimation(el, cfg);
        return () => {
            off();
            el.removeAttribute(TEXTANIM_BOUND_ATTR);
        };
    });
    return () => cleanups.forEach((c) => c());
}
/**
 * Reveal-animation hook for the LIVE React hosts (builder canvas + preview). Returns a ref to attach to
 * the heading/text element. The storefront gets the identical effect from {@link initTextAnimations}.
 */
export function useTextAnimation(cfg) {
    const ref = useRef(null);
    useIsoLayoutEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        // Claim the element so the vanilla init steps over it if both runtimes are present on one page.
        el.setAttribute(TEXTANIM_BOUND_ATTR, '');
        const off = applyTextAnimation(el, cfg);
        return () => {
            off();
            el.removeAttribute(TEXTANIM_BOUND_ATTR);
        };
    }, [cfg.type, cfg.splitUnit, cfg.from, cfg.stagger, cfg.duration, cfg.yOffset, cfg.typingSpeed]);
    return ref;
}
//# sourceMappingURL=textAnimation.js.map